import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Breadcrumbs, Button, CircularProgress, Container, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { ResponseError, RoleEnum, type MembershipDetails } from 'api-client';
import { useTeamsApi } from '@/context';
import MemberManagement, { type MemberAction } from './MemberManagement';
import MemberRemovalDialog from './MemberRemovalDialog';

async function memberError(failure: unknown, fallback: string): Promise<string> {
  if (failure instanceof ResponseError) {
    const body: unknown = await failure.response.json().catch((): null => null);
    if (body && typeof body === 'object') {
      const detail = Reflect.get(body, 'detail');
      if (typeof detail === 'string') return detail;
      const role = Reflect.get(body, 'role');
      if (Array.isArray(role) && role.every((value) => typeof value === 'string')) return role.join(' ');
    }
  }
  return fallback;
}

export default function TeamMemberDetails() {
  const api = useTeamsApi();
  const { teamSlug, memberId } = useParams<{ teamSlug: string; memberId: string }>();
  const membershipId = Number(memberId);
  const generation = useRef<number>(0);
  const [details, setDetails] = useState<MembershipDetails | null>(null);
  const [role, setRole] = useState<RoleEnum>(RoleEnum.Member);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<MemberAction>(null);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);
  const [retry, setRetry] = useState<number>(0);
  const settings = `/teams/settings/${encodeURIComponent(teamSlug || '')}?tab=1`;

  useEffect(() => {
    const current = ++generation.current;
    setDetails(null);
    setPending(null);
    setError(null);
    setSaved(false);
    setConfirm(false);
    if (!teamSlug || !Number.isSafeInteger(membershipId) || membershipId <= 0) {
      setError('Member not found or access is unavailable.');
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .teamMemberRetrieve({ teamSlug, membershipId })
      .then((result) => {
        if (current !== generation.current) return;
        setDetails(result);
        setRole(result.membership.role);
      })
      .catch(() => {
        if (current === generation.current) setError('Could not load this membership. Check your access or retry.');
      })
      .finally(() => {
        if (current === generation.current) setLoading(false);
      });
    return () => {
      generation.current += 1;
    };
  }, [api, teamSlug, membershipId, retry]);

  const mutate = async (action: Exclude<MemberAction, null>) => {
    if (!details || !teamSlug || pending || (action === 'role' ? !details.canEdit || role === details.membership.role : !details.canRemove)) return;
    const current = generation.current;
    setPending(action);
    setError(null);
    setSaved(false);
    try {
      if (action === 'role') {
        const result = await api.teamMemberUpdate({ teamSlug, membershipId, membershipRole: { role } });
        if (current !== generation.current) return;
        setDetails(result);
        setRole(result.membership.role);
        setSaved(true);
      } else {
        await api.teamMemberDestroy({ teamSlug, membershipId });
        if (current === generation.current) window.location.assign(details.isSelf ? '/teams/' : settings);
      }
    } catch (failure) {
      const message = await memberError(
        failure,
        action === 'role'
          ? 'Could not update the role. Please retry.'
          : 'Could not confirm member removal. Please retry.',
      );
      if (current !== generation.current) return;
      setError(message);
      if (action === 'remove' || (failure instanceof ResponseError && failure.response.status === 403)) {
        try {
          const fresh = await api.teamMemberRetrieve({ teamSlug, membershipId });
          if (current === generation.current) setDetails(fresh);
        } catch {
          if (current === generation.current) {
            setDetails(null);
            setConfirm(false);
            setError(message + ' Could not refresh this membership. Retry the page before making another change.');
          }
        }
      }
    } finally {
      if (current === generation.current) setPending(null);
    }
  };

  return (
    <Container maxWidth="md">
      <Box py={3}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/teams/">
            Teams
          </Link>
          <Link component={RouterLink} to={settings}>
            Members
          </Link>
          <Typography color="text.primary">Member details</Typography>
        </Breadcrumbs>
        <Typography component="h1" variant="h4" mb={3}>
          Member details
        </Typography>
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress aria-label="Loading member details" />
            <Typography>Loading member details…</Typography>
          </Stack>
        ) : !details ? (
          <Stack spacing={2}>
            <Alert severity="error">{error}</Alert>
            <Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {saved && <Alert severity="success">Role updated.</Alert>}
            <MemberManagement
              details={details}
              role={role}
              pending={pending}
              error={confirm ? null : error}
              onRole={setRole}
              onSave={() => {
                void mutate('role');
              }}
              onRemove={() => {
                setError(null);
                setConfirm(true);
              }}
            />
            <MemberRemovalDialog
              details={details}
              open={confirm}
              pending={pending === 'remove'}
              error={error}
              onCancel={() => {
                if (!pending) setConfirm(false);
              }}
              onConfirm={() => {
                void mutate('remove');
              }}
            />
          </Stack>
        )}
      </Box>
    </Container>
  );
}
